import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import { FiX } from "react-icons/fi"
import {
  ContragentListApi,
  type ContragentListItemDTO,
} from "../../../api/contragentList"
import {
  type ContragentResponseDTO,
  ContragentService,
} from "../../../api/contragents"

type Props = {
  label: string
  contragentRole: "SUPPLIER" | "CUSTOMER" | "MANUFACTURER"
  value: string
  displayValue?: string
  onChange: (nextId: string, nextName: string) => void
  isRequired?: boolean
  isInvalid?: boolean
  isClearable?: boolean
  size?: "sm" | "md" | "lg"
}

export default function ContragentSelect({
  label,
  contragentRole,
  value,
  displayValue,
  onChange,
  isRequired = false,
  isInvalid = false,
  isClearable = true,
  size = "md",
}: Props) {
  const picker = useDisclosure()
  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim())
    }, 350)

    return () => window.clearTimeout(timeoutId)
  }, [query])

  const { data: selectedContragent } = useQuery<ContragentResponseDTO>({
    queryKey: ["contragents", "detail", value],
    queryFn: () => ContragentService.getById(value),
    enabled: Boolean(value),
  })

  const { data, isPending } = useQuery({
    queryKey: ["contragents", "select", contragentRole, debouncedQuery],
    queryFn: () =>
      ContragentListApi.getContragentListPage({
        search: debouncedQuery || undefined,
        status: "ACTIVE",
        role: contragentRole,
        page: 1,
        page_size: 50,
        sort_by: "full_name",
        sort_dir: "asc",
      }),
    enabled: picker.isOpen,
  })

  const filtered = useMemo(
    () =>
      (data?.items ?? []).filter(
        (item: ContragentListItemDTO) =>
          Array.isArray(item.roles) && item.roles.includes(contragentRole),
      ),
    [data?.items, contragentRole],
  )

  const selectedLabel = selectedContragent
    ? `${selectedContragent.full_name}${selectedContragent.nip ? ` (${selectedContragent.nip})` : ""}`
    : (displayValue?.trim() ?? "")

  useEffect(() => {
    if (!picker.isOpen) setQuery("")
  }, [picker.isOpen])

  return (
    <>
      <FormControl isRequired={isRequired} isInvalid={isInvalid}>
        <FormLabel>{label}</FormLabel>
        <HStack>
          <Input
            size={size}
            readOnly
            value={selectedLabel}
            placeholder="Nie wybrano kontrahenta"
            cursor="pointer"
            onClick={picker.onOpen}
          />
          {value && isClearable ? (
            <IconButton
              aria-label="Wyczyść kontrahenta"
              icon={<FiX />}
              size={size}
              variant="outline"
              colorScheme="red"
              onClick={() => onChange("", "")}
            />
          ) : null}
        </HStack>
      </FormControl>

      <Modal
        isOpen={picker.isOpen}
        onClose={picker.onClose}
        initialFocusRef={searchInputRef}
        size="6xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Wybierz kontrahenta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack mb={3} align="stretch">
              <Input
                ref={searchInputRef}
                placeholder="Szukaj po nazwie, NIP, nazwie skróconej lub ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <Button onClick={() => setQuery("")}>Wyczyść</Button>
            </HStack>

            {isPending ? (
              <HStack justify="center" py={6}>
                <Spinner />
              </HStack>
            ) : (
              <TableContainer
                maxH="60vh"
                overflowY="auto"
                borderWidth="1px"
                borderRadius="md"
              >
                <Table size="sm" sx={{ tableLayout: "fixed" }}>
                  <Thead>
                    <Tr>
                      <Th w="320px">Nazwa</Th>
                      <Th w="140px">Nazwa skrócona</Th>
                      <Th w="140px">NIP</Th>
                      <Th w="140px">ID zewnętrzne</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filtered.map((item) => (
                      <Tr
                        key={item.id}
                        _hover={{ bg: rowHoverBg }}
                        cursor="pointer"
                        onClick={() => {
                          onChange(item.id, item.full_name)
                          picker.onClose()
                        }}
                      >
                        <Td
                          maxW="320px"
                          whiteSpace="nowrap"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          title={item.full_name}
                        >
                          {item.full_name}
                        </Td>
                        <Td>{item.short_name ?? "—"}</Td>
                        <Td>{item.nip ?? "—"}</Td>
                        <Td>{item.external_id ?? "—"}</Td>
                      </Tr>
                    ))}
                    {filtered.length === 0 ? (
                      <Tr>
                        <Td colSpan={4}>
                          <Box p={2} color="gray.500">
                            Brak wyników
                          </Box>
                        </Td>
                      </Tr>
                    ) : null}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={picker.onClose}>Zamknij</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
